import { useCallback, useEffect, useState } from 'react';
import {
  Modal,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';
import {
  GestureHandlerRootView,
  ScrollView,
} from 'react-native-gesture-handler';
import {
  initialWindowMetrics,
  SafeAreaProvider,
} from 'react-native-safe-area-context';
import { Flex, useSafeArea, useScreenSize } from '@banhala/ui-kit';
import { AnalyticsScreen } from 'shared/src/context';
import { systemAlert } from 'shared/src/component/systemAlert';
import { isAndroid } from 'shared/src/utils';
import type { LikeFolder } from 'shared/src/api/likedFolderService/getLikedFolders/types';
import { useLikedFolders } from '../../queries/useLikedFolders';
import { useDeleteFolder } from '../../queries/useDeleteFolder';
import { useEditFoldersOrder } from '../../queries/useEditFoldersOrder';
import {
  DRAGGABLE_ITEM_HEIGHT,
  DraggableFolderList,
} from './components/DraggableFolderList';
import { FolderOrderEditBasicFolder } from './components/FolderOrderEditBasicFolder';
import { FolderOrderEditHeader } from './components/FolderOrderEditHeader';
import { FolderOrderEditItem } from './components/FolderOrderEditItem';
import { useLogClickRemoveFolder } from './hooks/useLogClickRemoveFolder';
import { useLogCompleteEditFolderList } from './hooks/useLogCompleteEditFolderList';
import { useLogViewFolderEdit } from './hooks/useLogViewFolderEdit';

const BASIC_FOLDER_SNO = 0;
const LOADMORE_THRESHOLD = DRAGGABLE_ITEM_HEIGHT * 4;

interface FolderOrderEditModalProps {
  visible: boolean;
  onClose: () => void;
}

export const FolderOrderEditModal = ({
  visible,
  onClose,
}: FolderOrderEditModalProps) => {
  const handleClose = useCallback(() => {
    systemAlert(
      '',
      '창을 닫으면 편집사항은 저장되지 않아요.\n정말 나갈까요?',
      [
        { text: '취소', style: 'cancel' },
        { text: '나가기', onPress: onClose },
      ],
    );
  }, [onClose]);

  const content = (
    <AnalyticsScreen screen="FOLDER_EDIT">
      <FolderOrderEditModalContent
        visible={visible}
        onClose={onClose}
        onRequestClose={handleClose}
      />
    </AnalyticsScreen>
  );

  return (
    <Modal
      visible={visible}
      animationType={'slide'}
      onRequestClose={handleClose}
      transparent={false}
      statusBarTranslucent={isAndroid}
    >
      {/*
        Android statusBarTranslucent 모달은 별도 window라 루트 provider inset이 안 먹혀
        (top=0) 헤더가 상태바와 겹침 → 모달 자체 provider로 재측정.
        iOS fullScreen 모달은 루트 safe area를 정상 상속하므로 provider를 중첩하면
        오히려 present/dismiss 후 하단 공백이 남아 Android 전용으로 게이트.
      */}
      {isAndroid ? (
        <SafeAreaProvider initialMetrics={initialWindowMetrics}>
          {content}
        </SafeAreaProvider>
      ) : (
        content
      )}
    </Modal>
  );
};

interface FolderOrderEditModalContentProps extends FolderOrderEditModalProps {
  onRequestClose: () => void;
}

const FolderOrderEditModalContent = ({
  visible,
  onClose,
  onRequestClose,
}: FolderOrderEditModalContentProps) => {
  const insets = useSafeArea();
  const { width: screenWidth } = useScreenSize();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useLikedFolders();
  const { mutate: deleteFolder } = useDeleteFolder();
  const { mutate: editFoldersOrder, isPending } = useEditFoldersOrder();

  const [items, setItems] = useState<LikeFolder[]>([]);
  const [didEdit, setDidEdit] = useState(false);

  useLogViewFolderEdit(visible);
  const logClickRemoveFolder = useLogClickRemoveFolder();
  const logCompleteEditFolderList = useLogCompleteEditFolderList();

  const basicFolder =
    data?.find(folder => folder.sno === BASIC_FOLDER_SNO) ?? null;

  useEffect(() => {
    if (!visible) {
      setItems([]);
      setDidEdit(false);
      return;
    }
    if (data == null) return;
    const dataFolders = data.filter(folder => folder.sno !== BASIC_FOLDER_SNO);
    setItems(prev => {
      if (prev.length === 0) return dataFolders;
      const existingSnos = new Set(prev.map(item => item.sno));
      const appended = dataFolders.filter(
        folder => !existingSnos.has(folder.sno),
      );
      return appended.length > 0 ? [...prev, ...appended] : prev;
    });
  }, [visible, data]);

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (!hasNextPage || isFetchingNextPage) return;
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    const distanceFromEnd =
      contentSize.height - (contentOffset.y + layoutMeasurement.height);
    if (distanceFromEnd < LOADMORE_THRESHOLD) {
      fetchNextPage();
    }
  };

  const handleComplete = useCallback(() => {
    const folderSnos = items.map(item => item.sno);
    if (folderSnos.length === 0) {
      onClose();
      return;
    }
    editFoldersOrder(
      { folder_snos: folderSnos },
      {
        onSuccess: () => {
          logCompleteEditFolderList();
          onClose();
        },
      },
    );
  }, [editFoldersOrder, items, logCompleteEditFolderList, onClose]);

  const handleDelete = useCallback(
    (folder: LikeFolder) => {
      systemAlert(
        '서랍 삭제',
        '삭제한 서랍은 복구할 수 없고\n찜한 상품도 함께 삭제됩니다.',
        [
          { text: '취소', style: 'cancel' },
          {
            text: '삭제',
            onPress: () => {
              logClickRemoveFolder({ folderSno: folder.sno });
              deleteFolder({ sno: folder.sno });
              setItems(prev => prev.filter(item => item.sno !== folder.sno));
              setDidEdit(true);
            },
          },
        ],
      );
    },
    [deleteFolder, logClickRemoveFolder],
  );

  const handleReorder = useCallback((reordered: LikeFolder[]) => {
    setItems(reordered);
    setDidEdit(true);
  }, []);

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <Flex.Column
        flex={1}
        width={screenWidth}
        alignSelf={'center'}
        backgroundColor={'content_always_white'}
      >
        <FolderOrderEditHeader
          onPressClose={onRequestClose}
          onPressComplete={handleComplete}
          isCompleteEnabled={didEdit && !isPending}
        />
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ paddingBottom: insets.bottom }}
          onScroll={handleScroll}
          scrollEventThrottle={16}
        >
          {basicFolder != null && (
            <FolderOrderEditBasicFolder folder={basicFolder} />
          )}
          <DraggableFolderList
            items={items}
            onReorder={handleReorder}
            renderItem={({
              item,
              initialIndex,
              positions,
              itemsCount,
              onDragEnd,
            }) => (
              <FolderOrderEditItem
                key={item.sno}
                folder={item}
                initialIndex={initialIndex}
                positions={positions}
                itemsCount={itemsCount}
                onPressDelete={() => handleDelete(item)}
                onDragEnd={onDragEnd}
              />
            )}
          />
        </ScrollView>
      </Flex.Column>
    </GestureHandlerRootView>
  );
};
